import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { createSlug } from '../../util/urlHelpers';
import { LISTING_STATE_PUBLISHED } from '../../util/types';
import {
  LANGUAGES_KEY,
  LESSON_FOCUS_KEY,
  TRANSMISSION_KEY,
  formatVehicleName,
  getOptionLabels,
  getShortAddress,
  getVehicle,
} from '../../util/instructorListing';

import AspectRatioWrapper from '../AspectRatioWrapper/AspectRatioWrapper';
import { AvatarMedium } from '../Avatar/Avatar';
import NamedLink from '../NamedLink/NamedLink';
import ResponsiveImage from '../ResponsiveImage/ResponsiveImage';
import { MarketingIcon } from '../MarketingSections/MarketingSections';

import css from './InstructorCard.module.css';

// Lesson focus chips shown before "+N more"
const MAX_FOCUS_CHIPS = 3;

/**
 * The hourly price, without cents when it's a whole dollar amount, e.g. "$100".
 *
 * @param {Object} intl react-intl object
 * @param {Object} price Money
 * @returns {string|null}
 */
export const formatCardPrice = (intl, price) => {
  if (!price || typeof price.amount !== 'number') {
    return null;
  }
  return price.amount % 100 === 0
    ? intl.formatNumber(price.amount / 100, {
        style: 'currency',
        currency: price.currency,
        maximumFractionDigits: 0,
      })
    : formatMoney(intl, price);
};

/**
 * Search result card for a Drivelo instructor listing: who the instructor is, whether they're
 * verified, where and what they teach, their car and their price. Shows the listing's first
 * photo when it has one; otherwise the instructor's own photo leads the card.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.listing listing entity with author (and author.profileImage) and images
 * @param {string?} props.className
 * @param {string?} props.renderSizes image sizes attribute
 * @param {Function?} props.setActiveListing highlights the listing on the search map
 * @param {boolean?} [props.showAuthor] hide the instructor row, e.g. on their own profile
 * @returns {JSX.Element}
 */
const InstructorCard = props => {
  const { listing, className, renderSizes, setActiveListing, showAuthor = true } = props;
  const config = useConfiguration();
  const intl = useIntl();

  const id = listing?.id?.uuid;
  const { title = '', price, publicData = {}, state } = listing?.attributes || {};
  const author = listing?.author;
  const name = author?.attributes?.profile?.displayName;
  const listingFields = config.listing?.listingFields || [];

  const image = listing?.images?.[0];
  const variantPrefix = config.layout?.listingImage?.variantPrefix || 'listing-card';
  const imageVariants = image
    ? Object.keys(image.attributes?.variants || {}).filter(k => k.startsWith(variantPrefix))
    : [];
  const { aspectWidth = 1, aspectHeight = 1 } = config.layout?.listingImage || {};

  const address = getShortAddress(publicData);
  const transmissions = getOptionLabels(
    listingFields,
    TRANSMISSION_KEY,
    publicData[TRANSMISSION_KEY]
  );
  const lessonFocus = getOptionLabels(
    listingFields,
    LESSON_FOCUS_KEY,
    publicData[LESSON_FOCUS_KEY]
  );
  const shownFocus = lessonFocus.slice(0, MAX_FOCUS_CHIPS);
  const moreFocus = lessonFocus.length - shownFocus.length;
  const vehicleName = formatVehicleName(getVehicle(publicData));
  const languages = getOptionLabels(listingFields, LANGUAGES_KEY, publicData[LANGUAGES_KEY]);
  const formattedPrice = formatCardPrice(intl, price);
  const isVerified = state === LISTING_STATE_PUBLISHED;

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(listing?.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : {};

  const ariaLabel = formattedPrice
    ? intl.formatMessage(
        { id: 'InstructorCard.ariaLabel' },
        { title, name: name || '', price: formattedPrice }
      )
    : title;

  return (
    <NamedLink
      className={classNames(css.root, className)}
      name="ListingPage"
      params={{ id, slug: createSlug(title) }}
      ariaLabel={ariaLabel}
    >
      <div className={css.inner} {...setActivePropsMaybe}>
        {image ? (
          <AspectRatioWrapper className={css.media} width={aspectWidth} height={aspectHeight}>
            <ResponsiveImage
              rootClassName={css.image}
              alt={title}
              image={image}
              variants={imageVariants}
              sizes={renderSizes}
            />
          </AspectRatioWrapper>
        ) : null}

        <div className={css.body}>
          {showAuthor && author ? (
            <div className={css.authorRow}>
              <AvatarMedium user={author} className={css.avatar} disableProfileLink />
              <div className={css.authorText}>
                <span className={css.name}>{name}</span>
                {isVerified ? (
                  <span className={css.verified}>
                    <MarketingIcon name="shield" className={css.verifiedIcon} />
                    <FormattedMessage id="InstructorCard.verified" />
                  </span>
                ) : null}
              </div>
            </div>
          ) : null}

          <p className={css.title}>{title}</p>

          {address || transmissions.length > 0 ? (
            <p className={css.facts}>
              {address ? (
                <span className={css.fact}>
                  <MarketingIcon name="pin" className={css.factIcon} />
                  {address}
                </span>
              ) : null}
              {transmissions.length > 0 ? (
                <span className={css.fact}>{transmissions.map(t => t.label).join(' · ')}</span>
              ) : null}
            </p>
          ) : null}

          {vehicleName ? (
            <p className={css.car}>
              <MarketingIcon name="car" className={css.factIcon} />
              {vehicleName}
            </p>
          ) : null}

          {languages.length > 0 ? (
            <p className={css.languages}>
              <FormattedMessage
                id="InstructorCard.languages"
                values={{ languages: languages.map(l => l.label).join(', ') }}
              />
            </p>
          ) : null}

          {shownFocus.length > 0 ? (
            <ul className={css.chips}>
              {shownFocus.map(({ option, label }) => (
                <li key={option} className={css.chip}>
                  {label}
                </li>
              ))}
              {moreFocus > 0 ? (
                <li className={css.chipMore}>
                  <FormattedMessage id="InstructorCard.moreFocus" values={{ count: moreFocus }} />
                </li>
              ) : null}
            </ul>
          ) : null}

          <div className={css.footer}>
            {formattedPrice ? (
              <span className={css.price}>
                <FormattedMessage
                  id="InstructorCard.price"
                  values={{
                    price: formattedPrice,
                    unit: chunks => <span className={css.unit}>{chunks}</span>,
                  }}
                />
              </span>
            ) : (
              <span />
            )}
            <span className={css.cta}>
              <FormattedMessage id="InstructorCard.viewProfile" />
              <MarketingIcon name="arrow" className={css.ctaIcon} />
            </span>
          </div>
        </div>
      </div>
    </NamedLink>
  );
};

export default InstructorCard;
